import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  EMPTY_ENGAGEMENT,
  ENGAGEMENT_CATEGORIES,
  validateEngagement,
} from '../../lib/broaderEngagements.js';
import { findSimilarEngagements } from '../../lib/findSimilar.js';
import { useSimilarCheck } from '../../hooks/useSimilarCheck.js';
import SimilarEntriesWarning from '../common/SimilarEntriesWarning.jsx';
import AnnounceCheckbox from '../common/AnnounceCheckbox.jsx';

export default function EngagementForm({
  initialValue,
  submitLabel,
  onSubmit,
  onCancel,
  showAnnounce = false,
  busy = false,
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState(() => ({ ...EMPTY_ENGAGEMENT, ...initialValue }));
  const [error, setError] = useState(null);

  // Reset only when the edited entry actually changes (different id), not
  // on every new prop reference — see comment in ScholarForm.jsx.
  useEffect(() => {
    if (initialValue) setForm({ ...EMPTY_ENGAGEMENT, ...initialValue });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue?.id]);

  function update(patch) {
    setForm((prev) => ({ ...prev, ...patch }));
    setError(null);
  }

  const { candidates: similarCandidates } = useSimilarCheck(
    { title: form.title, url: form.url, excludeId: initialValue?.id },
    findSimilarEngagements,
  );

  async function handleSubmit(event) {
    event.preventDefault();
    const errKey = validateEngagement(form);
    if (errKey) {
      setError(t(errKey));
      return;
    }
    try {
      await onSubmit(form);
    } catch (err) {
      setError(err.message ?? String(err));
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-6">
      {/* Category: three stacked radio cards so the (sub)category
          descriptions have room to explain where an entry belongs. */}
      <Field label={t('broaderEngagements.form.category')} required>
        <div className="flex flex-col gap-2">
          {ENGAGEMENT_CATEGORIES.map((category) => (
            <label
              key={category}
              className={`cursor-pointer rounded-md border px-4 py-3 transition ${
                form.category === category
                  ? 'border-gold/60 bg-navy-mid/60'
                  : 'border-white/10 bg-navy-deep/40 hover:border-gold/30'
              }`}
            >
              <input
                type="radio"
                name="engagement-category"
                value={category}
                checked={form.category === category}
                onChange={(e) => update({ category: e.target.value })}
                className="sr-only"
              />
              <span className={`block text-sm font-medium ${form.category === category ? 'text-gold-light' : 'text-cream'}`}>
                {t(`broaderEngagements.categories.${category}.label`)}
              </span>
              <span className="mt-0.5 block text-xs text-ink-secondary">
                {t(`broaderEngagements.categories.${category}.hint`)}
              </span>
            </label>
          ))}
        </div>
      </Field>

      <Field label={t('broaderEngagements.form.title')} required>
        <Input value={form.title} onChange={(v) => update({ title: v })} />
      </Field>

      <div className="grid gap-6 md:grid-cols-[1fr_140px]">
        <Field label={t('broaderEngagements.form.author')} hint={t('broaderEngagements.form.authorHint')}>
          <Input value={form.author} onChange={(v) => update({ author: v })} />
        </Field>
        <Field label={t('broaderEngagements.form.year')}>
          <Input
            type="number"
            inputMode="numeric"
            value={form.year ?? ''}
            onChange={(v) => update({ year: v === '' ? null : v })}
            placeholder="2026"
          />
        </Field>
      </div>

      <Field label={t('broaderEngagements.form.url')} hint={t('broaderEngagements.form.urlHint')}>
        <Input type="text" inputMode="url" value={form.url} onChange={(v) => update({ url: v })} placeholder="https://…" />
      </Field>

      <Field label={t('broaderEngagements.form.description')} hint={t('broaderEngagements.form.descriptionHint')}>
        <Textarea rows={8} value={form.description} onChange={(v) => update({ description: v })} />
      </Field>

      {showAnnounce && (
        <AnnounceCheckbox
          checked={form.announce_in_whats_new}
          onChange={(v) => update({ announce_in_whats_new: v })}
        />
      )}

      <SimilarEntriesWarning candidates={similarCandidates} kind="broader_engagements" />

      {error && (
        <p className="rounded-md border border-red-500/40 bg-red-950/40 px-3 py-2 text-sm text-red-200">{error}</p>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
        <button type="button" className="btn-ghost" onClick={onCancel} disabled={busy}>
          {t('common.cancel')}
        </button>
        <button type="submit" className="btn-primary" disabled={busy}>
          {busy ? t('common.loading') : submitLabel}
        </button>
      </div>
    </form>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium uppercase tracking-[0.12em] text-ink-secondary">
        {label}
        {required && <span className="ml-1 text-gold">*</span>}
      </span>
      {children}
      {hint && <span className="mt-1 block text-xs text-ink-muted">{hint}</span>}
    </label>
  );
}

function Input({ value, onChange, ...rest }) {
  return (
    <input
      {...rest}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      className="form-input"
    />
  );
}

function Textarea({ value, onChange, rows = 4, ...rest }) {
  return (
    <textarea
      {...rest}
      rows={rows}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      className="form-input"
    />
  );
}
